import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

// Global store to persist state across morph operations
/** @type {Map<string, { quantity: number, sellingPlanName: string | undefined }>} */
const quantityPickerState = new Map();

/**
 * A custom element that manages quantity picker options with selling plan sync.
 *
 * @extends Component
 */
export class QuantityPickerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Set up radio button change handlers
    const radioButtons = this.querySelectorAll('input[type="radio"]');
    radioButtons.forEach((radio) => {
      radio.addEventListener("change", this.#handleRadioChange.bind(this));
    });

    // Listen for variant updates - use closest .shopify-section as the event bubbles from variant-picker
    // We need a common ancestor of both variant-picker and this component
    const section = this.closest(".shopify-section") || document.body;
    section.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate.bind(this)
    );

    // Check if we have saved state from before a morph operation
    const sectionId = this.dataset.sectionId || "";
    const savedState = quantityPickerState.get(sectionId);

    if (savedState) {
      // Restore the saved selection
      const targetRadio = this.querySelector(
        `input[type="radio"][data-quantity="${savedState.quantity}"]`
      );
      if (targetRadio instanceof HTMLInputElement) {
        targetRadio.checked = true;
        targetRadio.setAttribute("data-current-checked", "true");
        // Trigger change event to sync quantity with other components
        // Use setTimeout to ensure other components are also reconnected
        setTimeout(() => {
          this.#handleRadioChange({ target: targetRadio });
        }, 0);
        return; // Skip default initialization
      }
    }

    // Set initial selection based on current quantity
    this.#updateSelection();

    // If no radio is checked, check the first one
    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    if (!checkedRadio) {
      const firstRadio = this.querySelector('input[type="radio"]');
      if (firstRadio) {
        firstRadio.checked = true;
        firstRadio.setAttribute("data-current-checked", "true");
        // Trigger change event to sync quantity
        this.#handleRadioChange({ target: firstRadio });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Handles radio button change
   * @param {Event} event - The change event
   */
  #handleRadioChange(event) {
    const radio = /** @type {HTMLInputElement} */ (event.target);
    if (!(radio instanceof HTMLInputElement)) return;

    // Get quantity value
    const quantity = parseInt(radio.dataset.quantity || radio.value) || 1;
    const label = /** @type {HTMLElement | null} */ (
      radio.closest(".quantity-picker-option")
    );
    const servings = parseInt(label?.dataset.servings || "30") || 30;
    const sellingPlanName =
      radio.dataset.sellingPlanName || label?.dataset.sellingPlanName;

    // Save state for persistence across morph operations
    const sectionId = this.dataset.sectionId || "";
    if (sectionId) {
      quantityPickerState.set(sectionId, { quantity, sellingPlanName });
    }

    // Update quantity selector if it exists
    const productForm = /** @type {any} */ (
      document.querySelector("product-form-component")
    );
    const quantitySelector = productForm?.refs?.quantitySelector;
    if (quantitySelector && typeof quantitySelector.setValue === "function") {
      quantitySelector.setValue(quantity.toString());
    }

    // Dispatch custom event for quantity change
    this.dispatchEvent(
      new CustomEvent("quantity-picker:change", {
        bubbles: true,
        detail: {
          quantity,
          servings,
          sellingPlanName,
        },
      })
    );
  }

  /**
   * Handles variant updates
   * @param {Event} event - The variant update event
   */
  #handleVariantUpdate(event) {
    // Restore saved selection after morph has reset the DOM
    const sectionId = this.dataset.sectionId || "";
    const savedState = quantityPickerState.get(sectionId);

    if (savedState) {
      // Find and check the radio with the saved quantity
      const targetRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector(
          `input[type="radio"][data-quantity="${savedState.quantity}"]`
        )
      );

      if (targetRadio) {
        targetRadio.checked = true;
        targetRadio.setAttribute("data-current-checked", "true");

        // Clear other radios' current-checked state
        const allRadios = this.querySelectorAll('input[type="radio"]');
        allRadios.forEach((radio) => {
          if (radio !== targetRadio) {
            radio.setAttribute("data-current-checked", "false");
          }
        });

        // Re-dispatch the change event to sync other components
        this.#handleRadioChange({ target: targetRadio });
        return;
      }
    }

    // Fallback: sync with whatever is currently checked (default behavior)
    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      this.#handleRadioChange({ target: checkedRadio });
    }
  }

  /**
   * Updates selection based on current quantity selector value
   */
  #updateSelection() {
    const productForm = /** @type {any} */ (
      document.querySelector("product-form-component")
    );
    const quantitySelector = productForm?.refs?.quantitySelector;
    if (!quantitySelector) return;

    const currentQuantity = parseInt(quantitySelector.getValue?.() || "1");
    const radioButtons = this.querySelectorAll('input[type="radio"]');

    for (const radio of radioButtons) {
      const radioInput = /** @type {HTMLInputElement} */ (radio);
      const optionQuantity =
        parseInt(radioInput.dataset.quantity || radioInput.value) || 1;
      if (optionQuantity === currentQuantity) {
        radioInput.checked = true;
        radioInput.setAttribute("data-current-checked", "true");
        break;
      }
    }
  }
}

if (!customElements.get("quantity-picker")) {
  customElements.define("quantity-picker", QuantityPickerComponent);
}
